import { Grid } from '@mui/material';
import { HSpacer, RoutalPalette } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { HeaderContent } from '../../../components/HeaderContent';
import { ClientsTableColumnKeys } from '../../../context/client/tableColumns';
import { TableColumnsSortConfig } from '../../../context/shared/tableColumns';
import { useRetrieveFlatClients } from '../../../hooks/client/useRetrieveFlatClients';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectResources } from '../../../hooks/project/useRetrieveActiveProjectResources';
import { useRetrieveRightPanelInfo } from '../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useRetrieveSupportDocs } from '../../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import eventsPanel, { PageViewPages } from '../../../services/events';
import ClientsCreateMenu from './ClientsCreateMenu';
import { ProjectAssetsClientsList } from './client-list';
import { ClientRightPanel } from './right-panel/ClientRightPanel';

export const ProjectSettingsClients = () => {
  const { retrieveDoc } = useRetrieveSupportDocs();
  const { data: projectResources } = useRetrieveActiveProjectResources();
  const { data: user } = useRetrieveUser();
  const { data: activeProject } = useRetrieveActiveProject();

  const { detailType } = useRetrieveRightPanelInfo();

  const [sortClientsInfo, setSortClientsInfo] = useState<TableColumnsSortConfig<ClientsTableColumnKeys>>({
    orderBy: `created_at`,
    order: `desc`,
  });

  const [searchParameters, setSearchParameters] = useState<{ limit: number; offset: number }>({
    offset: 0,
    limit: 100,
  });

  const { data: flatClients, isLoading } = useRetrieveFlatClients();

  useEffect(() => {
    eventsPanel.pageView(PageViewPages.clients);
  }, []);

  /**
   * Check against the quota if new clients can be added
   */
  const canAdd = () => {
    if (!activeProject || !projectResources || !flatClients || isLoading) {
      return false;
    }
    return flatClients.length < projectResources.max_clients;
  };

  return (
    <Grid
      container
      style={{
        height: `100%`,
        width: `100%`,
        zIndex: 2,
        backgroundColor: `white`,
        display: `flex`,
        flexDirection: `row`,
        minWidth: `1080px`,
      }}
      wrap="nowrap"
    >
      <Grid container direction="column" wrap="nowrap">
        <Grid
          container
          direction="column"
          style={{
            padding: `11px 20px`,
            backgroundColor: RoutalPalette.neutral02n,
            borderBottom: `1px solid ${RoutalPalette.neutral10}`,
          }}
          wrap="nowrap"
        >
          <Grid container direction="row" wrap="nowrap">
            <HeaderContent
              titleIntlId="clients.title"
              article={{
                intlId: `clients.article`,
                link: retrieveDoc({ supportPage: `clients`, language: user?.language.value }),
              }}
            />

            <Grid container item justifyContent="flex-end">
              {activeProject ? (
                <>
                  <HSpacer medium />
                  <ClientsCreateMenu
                    id="list-clients-create-actions"
                    intlId="clients.new"
                    disabled={!canAdd()}
                    style={{
                      backgroundColor: RoutalPalette.secondary40,
                      justifyContent: `space-between`,
                    }}
                    hideSeparator
                    containerStyle={{
                      width: `unset`,
                    }}
                  />
                </>
              ) : null}
            </Grid>
          </Grid>
        </Grid>

        <Grid container direction="row" wrap="nowrap" style={{ height: `calc(100% - 70px)` }}>
          <ProjectAssetsClientsList
            searchParameters={searchParameters}
            sortClientsInfo={sortClientsInfo}
            setSortClientsInfo={setSortClientsInfo}
            setSearchParameters={setSearchParameters}
          />

          {detailType === `client` ? <ClientRightPanel /> : null}
        </Grid>
      </Grid>
    </Grid>
  );
};
